import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { validateEmail, validatePhone } from "../shared/utils/validation";
import ConfirmationModal from "../components/ConfirmationModal";
import { TableActionColumnHeader } from "../components/TableShell";
import SearchableSelect from "../components/SearchableSelect";
import CheckboxMultiSelect from "../components/CheckboxMultiSelect";
import { IconEdit, IconDelete, IconSave, IconCancel, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { attachValidatedCsvFileImport, downloadCsvTextFile } from "../shared/utils/csvFileHelpers";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;

const TAB_PROGRAM_LEAD = "programLead";

/** One row per user: merge program_lead memberships and program names. */
function consolidateLeadsByUser(leads, programsList) {
  const programNameById = Object.fromEntries(
    (programsList || []).map((p) => [String(p._id), String(p?.name || "").trim() || "Program"])
  );
  const byUser = new Map();
  for (const lead of leads || []) {
    const userId = String(lead.user_id || "").trim();
    if (!userId) continue;
    const programId = String(lead.program_id || "").trim();
    let row = byUser.get(userId);
    if (!row) {
      row = {
        user_id: userId,
        first_name: lead.first_name || "",
        last_name: lead.last_name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        program_ids: [],
        lead_ids: [],
      };
      byUser.set(userId, row);
    }
    if (programId && !row.program_ids.includes(programId)) {
      row.program_ids.push(programId);
    }
    if (lead._id != null) row.lead_ids.push(lead._id);
  }
  return [...byUser.values()].map((row) => {
    const names = row.program_ids
      .map((id) => programNameById[id] || "—")
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return {
      ...row,
      program_names: names,
      program_names_display: names.length ? names.join(", ") : "—",
    };
  });
}

function ProgramOrganizers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const programFromUrl = searchParams.get("program") || "";
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('__all__');
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [formExpanded, setFormExpanded] = useState(false);
  const [importPanelExpanded, setImportPanelExpanded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [memberForm, setMemberForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [newMemberProgramId, setNewMemberProgramId] = useState('');
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [memberEditDraft, setMemberEditDraft] = useState(null);
  const [bannerError, setBannerError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    onConfirm: null,
  });

  const programOptions = useMemo(
    () =>
      (programs || []).map((p) => ({
        value: String(p._id),
        label: String(p?.name || "Program"),
      })),
    [programs]
  );

  const loadPrograms = useCallback(() => {
    if (!account_id) return;
    apiFetch(serverUrl + '/getProgram', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => setPrograms(Array.isArray(data) ? data : []))
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }, [account_id, serverUrl]);

  const loadMembers = useCallback(() => {
    if (!account_id) {
      setMembers([]);
      return;
    }
    apiFetch(serverUrl + '/getProgramOrganizers', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]));
  }, [account_id, serverUrl]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  useEffect(() => {
    if (!programFromUrl) {
      setSelectedProgramId("__all__");
      return;
    }
    if (programs.length > 0) {
      const exists = programs.some((g) => String(g._id) === String(programFromUrl));
      if (exists) setSelectedProgramId(String(programFromUrl));
      else {
        setSelectedProgramId("__all__");
        setSearchParams({ tab: TAB_PROGRAM_LEAD }, { replace: true });
      }
    }
  }, [programFromUrl, programs, setSearchParams]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  function handleProgramChange(next) {
    const id = typeof next === 'string' ? next : (next?.target?.value || '');
    setSelectedProgramId(id);
    setEditingMemberId(null);
    setMemberEditDraft(null);
    setSelectedIds(new Set());
    if (id && id !== "__all__") {
      setSearchParams({ tab: TAB_PROGRAM_LEAD, program: id }, { replace: true });
    } else {
      setSearchParams({ tab: TAB_PROGRAM_LEAD }, { replace: true });
    }
  }

  function getProgramNamesDisplay(row) {
    return row?.program_names_display || "—";
  }

  function createMember() {
    const firstVal = (memberForm.first_name || '').trim();
    const lastVal = (memberForm.last_name || '').trim();
    if (!firstVal || !lastVal) {
      setConfirmDialog({ open: true, title: "Program lead", message: "First name and last name are required.", mode: "alert", onConfirm: null });
      return;
    }
    const emailCheck = validateEmail(memberForm.email);
    if (!emailCheck.valid) { setConfirmDialog({ open: true, title: "Program lead", message: emailCheck.message, mode: "alert", onConfirm: null }); return; }
    const phoneCheck = validatePhone(memberForm.phone);
    if (!phoneCheck.valid) { setConfirmDialog({ open: true, title: "Program lead", message: phoneCheck.message, mode: "alert", onConfirm: null }); return; }
    const programId = selectedProgramId && selectedProgramId !== '__all__' ? selectedProgramId : (newMemberProgramId || '').trim();
    if (!programId) {
      setConfirmDialog({ open: true, title: "Program lead", message: "Please select a program for the new lead.", mode: "alert", onConfirm: null });
      return;
    }
    apiFetch(serverUrl + '/createProgramOrganizer', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        program_id: programId,
        first_name: firstVal,
        last_name: lastVal,
        email: (memberForm.email || '').trim(),
        phone: (memberForm.phone || '').trim(),
      }),
    })
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) {
          setConfirmDialog({ open: true, title: "Program lead", message: text, mode: "alert", onConfirm: null });
          return;
        }
        setConfirmDialog({ open: true, title: "Program lead", message: text, mode: "alert", onConfirm: null });
        loadMembers();
        setMemberForm({ first_name: '', last_name: '', email: '', phone: '' });
        if (selectedProgramId === '__all__') setNewMemberProgramId('');
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function updateMember(userId, payload) {
    const firstVal = (payload.first_name != null ? String(payload.first_name) : '').trim();
    const lastVal = (payload.last_name != null ? String(payload.last_name) : '').trim();
    if (!firstVal || !lastVal) {
      setConfirmDialog({ open: true, title: "Program lead", message: "First name and last name are required.", mode: "alert", onConfirm: null });
      return;
    }
    const emailCheck = validateEmail(payload.email);
    if (!emailCheck.valid) { setConfirmDialog({ open: true, title: "Program lead", message: emailCheck.message, mode: "alert", onConfirm: null }); return; }
    const phoneCheck = validatePhone(payload.phone);
    if (!phoneCheck.valid) { setConfirmDialog({ open: true, title: "Program lead", message: phoneCheck.message, mode: "alert", onConfirm: null }); return; }
    const programIds = Array.isArray(payload.program_ids)
      ? [...new Set(payload.program_ids.map((p) => String(p || "").trim()).filter(Boolean))]
      : [];
    if (programIds.length === 0) {
      setConfirmDialog({ open: true, title: "Program lead", message: "Select at least one program.", mode: "alert", onConfirm: null });
      return;
    }
    apiFetch(serverUrl + '/updateProgramOrganizer', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        user_id: userId,
        program_ids: programIds,
        first_name: payload.first_name,
        last_name: payload.last_name,
        email: payload.email,
        phone: payload.phone,
      }),
    })
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) {
          setConfirmDialog({ open: true, title: "Program lead", message: text, mode: "alert", onConfirm: null });
          return;
        }
        setConfirmDialog({ open: true, title: "Program lead", message: text, mode: "alert", onConfirm: null });
        loadMembers();
        setEditingMemberId(null);
        setMemberEditDraft(null);
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  async function deleteLeadIds(leadIds) {
    const ids = (leadIds || []).filter(Boolean);
    let success = 0;
    let fail = 0;
    const errors = [];
    for (const leadId of ids) {
      try {
        const res = await apiFetch(serverUrl + '/deleteProgramOrganizer', {
          method: 'POST',
          body: JSON.stringify({ account_id, _id: leadId }),
        });
        const text = await res.text();
        if (res.ok) success++;
        else {
          fail++;
          errors.push(text);
        }
      } catch (err) {
        fail++;
        errors.push(getMessageForFetchError(err));
      }
    }
    return { success, fail, errors };
  }

  function deleteMember(row) {
    const leadIds = row?.lead_ids || [];
    const programLabel = getProgramNamesDisplay(row);
    setConfirmDialog({
      open: true,
      title: "Delete lead",
      message:
        leadIds.length > 1
          ? `Delete this lead from ${leadIds.length} programs (${programLabel})?`
          : "Delete this lead?",
      onConfirm: async () => {
        const { success, fail, errors } = await deleteLeadIds(leadIds);
        let msg = fail === 0 ? "Deleted successfully" : `Delete completed. Success: ${success}, Errors: ${fail}`;
        if (errors.length > 0 && errors.length <= 10) msg += "\n\n" + errors.join("\n");
        setConfirmDialog({ open: true, title: "Program lead", message: msg, mode: "alert", onConfirm: null });
        loadMembers();
        setSelectedIds((prev) => {
          const s = new Set(prev);
          s.delete(row.user_id);
          return s;
        });
      },
    });
  }

  function startEditMember(m) {
    setEditingMemberId(m.user_id);
    setMemberEditDraft({
      first_name: m.first_name || '',
      last_name: m.last_name || '',
      email: m.email || '',
      phone: m.phone || '',
      program_ids: [...(m.program_ids || [])],
    });
  }

  function parseMemberCSV(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const firstIdx = findCsvColumnIndex(headers, ['first_name', 'firstname']);
    const lastIdx = findCsvColumnIndex(headers, ['last_name', 'lastname']);
    const emailIdx = findCsvColumnIndex(headers, ['email']);
    const phoneIdx = findCsvColumnIndex(headers, ['phone']);
    const programNameIdx = findCsvColumnIndex(headers, ['program_name', 'program', 'group_name', 'group']);
    if (firstIdx === -1 || lastIdx === -1) throw new Error('CSV must contain first_name and last_name (or firstname/lastname) columns');
    const records = [];
    for (const values of rows) {
      records.push({
        first_name: firstIdx >= 0 ? (values[firstIdx] || '') : '',
        last_name: lastIdx >= 0 ? (values[lastIdx] || '') : '',
        email: emailIdx >= 0 ? (values[emailIdx] || '') : '',
        phone: phoneIdx >= 0 ? (values[phoneIdx] || '') : '',
        program_name: programNameIdx >= 0 ? (values[programNameIdx] || '') : '',
      });
    }
    return records;
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseMemberCSV(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      const csvHasProgramColumn = records.some(r => (r.program_name || '').trim() !== '');
      let defaultProgramId = selectedProgramId && selectedProgramId !== '__all__' ? selectedProgramId : null;
      if (!csvHasProgramColumn && !defaultProgramId) {
        setConfirmDialog({ open: true, title: "Import", message: "Select a single program above, or add a program_name (or program) column to your CSV to import into multiple programs.", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const r = records[i];
        const firstVal = (r.first_name || '').trim();
        const lastVal = (r.last_name || '').trim();
        if (!firstVal || !lastVal) {
          errorCount++;
          errors.push(`Row ${i + 2}: First name and last name required`);
          continue;
        }
        const emailCheck = validateEmail(r.email);
        if (!emailCheck.valid) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${emailCheck.message}`);
          continue;
        }
        const phoneCheck = validatePhone(r.phone);
        if (!phoneCheck.valid) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${phoneCheck.message}`);
          continue;
        }
        let programId = defaultProgramId;
        if (csvHasProgramColumn && (r.program_name || '').trim() !== '') {
          const programNameTrim = (r.program_name || '').trim();
          const found = programs.find(p => (p.name || '').trim().toLowerCase() === programNameTrim.toLowerCase());
          if (!found) {
            errorCount++;
            errors.push(`Row ${i + 2}: Program "${programNameTrim}" not found`);
            continue;
          }
          programId = found._id;
        } else if (csvHasProgramColumn && (r.program_name || '').trim() === '') {
          errorCount++;
          errors.push(`Row ${i + 2}: program_name is required when using multi-program CSV`);
          continue;
        }
        if (!programId) {
          errorCount++;
          errors.push(`Row ${i + 2}: No program specified`);
          continue;
        }
        try {
          const response = await apiFetch(serverUrl + '/createProgramOrganizer', {
            method: 'POST',
            body: JSON.stringify({
              account_id,
              program_id: programId,
              first_name: firstVal,
              last_name: lastVal,
              email: (r.email || '').trim(),
              phone: (r.phone || '').trim(),
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (err) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(err)}`);
        }
      }
      let message = `Import completed!\nSuccess: ${successCount}\nErrors: ${errorCount}`;
      if (errors.length > 0 && errors.length <= 10) message += '\n\nErrors:\n' + errors.join('\n');
      else if (errors.length > 10) message += '\n\nFirst 10 errors:\n' + errors.slice(0, 10).join('\n');
      setConfirmDialog({ open: true, title: "Import completed", message, mode: "alert", onConfirm: null });
      loadMembers();
    } catch (err) {
      setBannerError(getMessageForFetchError(err, 'Error importing CSV.'));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile('program-leads-sample.csv', [
      'first_name,last_name,email,phone,program_name',
      'John,Doe,john@example.com,555-1234,Financial Literacy',
      'Jane,Smith,jane@example.com,555-5678,Financial Literacy',
    ]);
  }

  function handleSort(key) {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  }

  const consolidatedMembers = useMemo(
    () => consolidateLeadsByUser(members, programs),
    [members, programs]
  );

  function getFilteredAndSortedData() {
    let filtered = consolidatedMembers.filter((m) => {
      if (selectedProgramId && selectedProgramId !== "__all__") {
        if (!(m.program_ids || []).includes(String(selectedProgramId))) return false;
      }
      const searchLower = searchTerm.toLowerCase();
      if (!searchLower) return true;
      return (
        (m.first_name && m.first_name.toLowerCase().includes(searchLower)) ||
        (m.last_name && m.last_name.toLowerCase().includes(searchLower)) ||
        (m.email && m.email.toLowerCase().includes(searchLower)) ||
        (m.phone && String(m.phone).toLowerCase().includes(searchLower)) ||
        (m.program_names_display && m.program_names_display.toLowerCase().includes(searchLower))
      );
    });
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === "program_name") {
          aVal = a.program_names_display || "";
          bVal = b.program_names_display || "";
        }
        aVal = aVal ?? "";
        bVal = bVal ?? "";
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData(), (m) => m.user_id);
  }

  function handleSelectAll(e) {
    const data = getFilteredAndSortedData();
    const next = new Set(selectedIds);
    if (e.target.checked) data.forEach((m) => next.add(m.user_id));
    else data.forEach((m) => next.delete(m.user_id));
    setSelectedIds(next);
  }

  function handleSelectOne(id, checked) {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete leads", message: "Please select at least one lead to delete", mode: "alert", onConfirm: null });
      return;
    }
    const rows = consolidatedMembers.filter((m) => selectedVisibleIds.has(m.user_id));
    const leadIds = rows.flatMap((m) => m.lead_ids || []);
    setConfirmDialog({
      open: true,
      title: "Delete leads",
      message: `Delete ${selectedVisibleIds.size} lead(s)? This removes them from all assigned programs.`,
      onConfirm: async () => {
        setDeleting(true);
        const { success, fail, errors } = await deleteLeadIds(leadIds);
        let msg = `Delete completed. Success: ${success}, Errors: ${fail}`;
        if (errors.length > 0 && errors.length <= 10) msg += "\n\n" + errors.join("\n");
        else if (errors.length > 10) msg += "\n\n" + errors.slice(0, 10).join("\n");
        setConfirmDialog({ open: true, title: "Delete completed", message: msg, mode: "alert", onConfirm: null });
        setSelectedIds(new Set());
        loadMembers();
        setDeleting(false);
      },
    });
  }

  const hasProgramSelected = selectedProgramId && selectedProgramId !== '';
  const effectiveNewMemberProgramId = selectedProgramId && selectedProgramId !== '__all__' ? selectedProgramId : newMemberProgramId;
  const allFieldsFilled = Boolean((memberForm.first_name || '').trim() && (memberForm.last_name || '').trim() && (memberForm.email || '').trim() && (memberForm.phone || '').trim()) && Boolean(effectiveNewMemberProgramId);
  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows, (m) => m.user_id);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  return (
    <div className="page-content">
      <div className="page-top">
        <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />

        <ConfirmationModal
          open={confirmDialog.open}
          title={confirmDialog.title || "Confirm"}
          message={confirmDialog.message}
          mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
          confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
          onConfirm={async () => {
            const fn = confirmDialog.onConfirm;
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
            if (fn) await fn();
          }}
          onCancel={() =>
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
          }
        />
        <div className="toolbar">
          <label className="toolbar-label">Program:</label>
          <div className="input-min-200" style={{ minWidth: 200 }}>
            <SearchableSelect
              value={selectedProgramId}
              onChange={(v) => handleProgramChange(String(v || ''))}
              options={[
                { value: "__all__", label: "All programs" },
                ...(programs || []).map((p) => ({ value: String(p._id), label: String(p?.name || "Program") })),
              ]}
              placeholder="— Select program —"
              searchPlaceholder="Search program…"
              allowEmpty={true}
              emptyLabel="— Select program —"
              maxVisibleItems={4}
              dropdownPortal={true}
              title="Program"
            />
          </div>
        </div>

        {hasProgramSelected && (
          <>
            <ImportCsvPanel
              expanded={importPanelExpanded}
              fileInputId="program-organizers-csv-import"
              importing={importing}
              columnHint="Columns: first_name, last_name, email, phone (all required); optional: program_name (or program) to import into multiple programs"
              onDownloadSample={downloadSampleCSV}
              onFileChange={handleFileChange}
              onImportText={importFromCsvText}
            />
            {formExpanded && (
              <div className="panel">
                <div className="panel-title">Add new lead <span className="panel-required-hint">(First name, last name, email and phone required)</span></div>
                <div className="panel-row">
                  {selectedProgramId === '__all__' && (
                    <div className="input-min-180" style={{ minWidth: 180 }}>
                      <SearchableSelect
                        value={newMemberProgramId}
                        onChange={(v) => setNewMemberProgramId(String(v || ''))}
                        options={(programs || []).map((p) => ({ value: String(p._id), label: String(p?.name || "Program") }))}
                        placeholder="— Select program —"
                        searchPlaceholder="Search program…"
                        allowEmpty={true}
                        emptyLabel="— Select program —"
                        maxVisibleItems={4}
                        dropdownPortal={true}
                        title="Program for new lead"
                      />
                    </div>
                  )}
                  <input type="text" value={memberForm.first_name} onChange={(e) => setMemberForm(f => ({ ...f, first_name: e.target.value }))} placeholder="First name *" className="input input-min-120" />
                  <input type="text" value={memberForm.last_name} onChange={(e) => setMemberForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name *" className="input input-min-120" />
                  <input type="text" value={memberForm.email} onChange={(e) => setMemberForm(f => ({ ...f, email: e.target.value }))} placeholder="Email *" className="input input-min-140" />
                  <input type="text" value={memberForm.phone} onChange={(e) => setMemberForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone *" className="input input-min-120" />
                  <button type="button" onClick={createMember} disabled={!allFieldsFilled} className="btn btn-primary">Add</button>
                </div>
              </div>
            )}
            <div className="toolbar">
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search first name, last name, email, phone, program…" className="input input-search-wide" />
              <button type="button" onClick={() => setSearchTerm('')} className="btn btn-secondary btn-sm">Clear</button>
              <span className="toolbar-divider" />
              <button type="button" onClick={() => { setImportPanelExpanded(!importPanelExpanded); if (!importPanelExpanded) setFormExpanded(false); }} className={`btn btn-md ${importPanelExpanded ? 'btn-primary-outline' : 'btn-primary'}`}>{importPanelExpanded ? '− Import CSV' : '+ Import CSV'}</button>
              <button type="button" onClick={() => { setFormExpanded(!formExpanded); if (!formExpanded) { setMemberForm({ first_name: '', last_name: '', email: '', phone: '' }); if (selectedProgramId === '__all__') setNewMemberProgramId(''); } if (!formExpanded) setImportPanelExpanded(false); }} className={`btn btn-md ${formExpanded ? 'btn-primary-outline' : 'btn-primary'}`}>{formExpanded ? '− New' : '+ New'}</button>
              <button type="button" onClick={deleteSelected} disabled={selectedVisibleCount === 0 || deleting} className="btn btn-danger">Delete{selectedCountSuffix}</button>
            </div>
          </>
        )}
      </div>
      <div className="table-scroll">
        {!hasProgramSelected ? (
          <p className="empty-message">Select a program above to view and manage leads.</p>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th><input type="checkbox" checked={filteredRows.length > 0 && filteredRows.every(m => selectedIds.has(m.user_id))} onChange={handleSelectAll} disabled={filteredRows.length === 0} /></th>
                    <th className="sortable" onClick={() => handleSort('program_name')}>Program</th>
                    <th className="sortable" onClick={() => handleSort('first_name')}>First name</th>
                    <th className="sortable" onClick={() => handleSort('last_name')}>Last name</th>
                    <th className="sortable" onClick={() => handleSort('email')}>Email *</th>
                    <th className="sortable" onClick={() => handleSort('phone')}>Phone *</th>
                    <TableActionColumnHeader align="left" />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(m => {
                    const isEditing = editingMemberId === m.user_id;
                    const draft = isEditing ? memberEditDraft : null;
                    return (
                      <tr key={m.user_id} className={isEditing ? 'row-editing' : ''}>
                        <td>
                          {!isEditing && <input type="checkbox" checked={selectedIds.has(m.user_id)} onChange={(e) => handleSelectOne(m.user_id, e.target.checked)} />}
                        </td>
                        {isEditing && draft ? (
                          <>
                            <td style={{ minWidth: 200, maxWidth: 320 }}>
                              <CheckboxMultiSelect
                                options={programOptions}
                                value={draft.program_ids || []}
                                onChange={(next) => setMemberEditDraft((d) => ({ ...d, program_ids: next }))}
                                placeholder="— Select program(s) —"
                                listboxLabel="Programs"
                                title="Programs"
                              />
                            </td>
                            <td>
                              <input type="text" value={draft.first_name} onChange={(e) => setMemberEditDraft(d => ({ ...d, first_name: e.target.value }))} placeholder="First name" className="input input-inline-edit input-full" />
                            </td>
                            <td>
                              <input type="text" value={draft.last_name} onChange={(e) => setMemberEditDraft(d => ({ ...d, last_name: e.target.value }))} placeholder="Last name" className="input input-inline-edit input-full" />
                            </td>
                            <td>
                              <input type="text" value={draft.email} onChange={(e) => setMemberEditDraft(d => ({ ...d, email: e.target.value }))} placeholder="Email *" className="input input-inline-edit input-full" />
                            </td>
                            <td>
                              <input type="text" value={draft.phone} onChange={(e) => setMemberEditDraft(d => ({ ...d, phone: e.target.value }))} placeholder="Phone *" className="input input-inline-edit input-full" />
                            </td>
                            <td>
                              <RowActionIcons>
                                <button type="button" onClick={() => updateMember(m.user_id, memberEditDraft)} className="btn-link" style={iconBtnStyle} title="Save"><IconSave size={18} /></button>
                                <button type="button" onClick={() => { setEditingMemberId(null); setMemberEditDraft(null); }} className="btn-link-muted" style={iconBtnStyle} title="Cancel"><IconCancel size={18} /></button>
                              </RowActionIcons>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{getProgramNamesDisplay(m)}</td>
                            <td>{m.first_name}</td>
                            <td>{m.last_name}</td>
                            <td>{m.email || '—'}</td>
                            <td>{m.phone || '—'}</td>
                            <td>
                              <RowActionIcons>
                                <button type="button" onClick={() => startEditMember(m)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                                <button type="button" onClick={() => deleteMember(m)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                              </RowActionIcons>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {consolidatedMembers.length === 0 && <p className="empty-message">No leads. Use + New (select a single program) or Import CSV to add leads.</p>}
            {consolidatedMembers.length > 0 && filteredRows.length === 0 && (
              <p className="empty-message">No leads match the current program filter or search.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ProgramOrganizers;
